import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiCheckCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

function ContactModern() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitStatus('success');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  const contactInfo = [
    { icon: <FiMapPin className="w-5 h-5" />, title: 'Office Address', lines: ['RKS Legal Office', '203, Indu Enclave, Vijay Nagar', 'Patna, Bihar – 800014'], color: 'from-primary-500 to-primary-600' },
    { icon: <FiPhone className="w-5 h-5" />, title: 'Phone', lines: ['+91 7970809406', 'Mon–Sat: 9 AM – 7 PM'], color: 'from-teal-500 to-teal-600' },
    { icon: <FiMail className="w-5 h-5" />, title: 'Email', lines: ['rkslegaloffice@gmail.com'], color: 'from-accent-500 to-accent-600' },
    { icon: <FiClock className="w-5 h-5" />, title: 'Business Hours', lines: ['Monday – Saturday: 9:00 AM – 7:00 PM', 'Sunday: By Appointment Only'], color: 'from-primary-400 to-primary-500' },
  ];

  const faqs = [
    { q: 'How do I schedule a consultation?', a: 'You can call us directly or fill out the contact form. Our team will schedule your consultation at a convenient time.' },
    { q: 'What areas of law do you specialize in?', a: 'We handle criminal law, civil disputes, property matters, banking & financial cases, service matters, Election, and corporate legal issues across Bihar.' },
    { q: 'Do you offer free consultations?', a: 'Initial consultation may be free depending on the nature of the case. Detailed legal advice is provided on a professional fee basis.' },
    { q: 'How much do your services cost?', a: 'Fees depend on the complexity and nature of the case. We ensure transparent and reasonable pricing.' },
    { q: 'Is my information confidential?', a: 'Yes, all client information is kept strictly confidential as per professional ethics and legal standards.' },
    { q: 'How experienced is your team?', a: 'Our team consists of experienced advocates with expertise in multiple areas of law, handling cases across Bihar courts.' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-20 text-center relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-bold text-white mb-4">Get In Touch</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/50 text-lg max-w-xl mx-auto">Have a legal issue or need expert advice? Our team is here to assist you across Bihar. Contact us today.</motion.p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-6xl mx-auto px-4 -mt-10 relative z-10">
        <motion.div variants={{ visible: { transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactInfo.map((item, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 text-center hover:shadow-card-hover transition-shadow">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white mx-auto mb-4`}>{item.icon}</div>
              <h3 className="font-semibold text-secondary-900 mb-2">{item.title}</h3>
              {item.lines.map((line, j) => <p key={j} className="text-sm text-secondary-500">{line}</p>)}
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Main Content: Form + Map */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-8">
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">Send Us a Message</h2>
              <p className="text-secondary-500 text-sm mb-6">Fill out the form and our legal team will get back to you within 24 hours.</p>

              {submitStatus === 'success' && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-teal-50 border border-teal-200 mb-6">
                  <FiCheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                  <p className="text-sm text-teal-700">Thank you! Your message has been sent. We'll be in touch soon.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><label className="label">Full Name *</label><input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required className="input" /></div>
                  <div><label className="label">Email Address *</label><input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required className="input" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><label className="label">Phone Number</label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(123) 456-7890" className="input" /></div>
                  <div>
                    <label className="label">Subject *</label>
                    <select name="subject" value={formData.subject} onChange={handleChange} required className="select-input">
                      <option value="">Select a subject...</option>
                      <option value="consultation">General Consultation</option>
                      <option value="criminal">Criminal Law & Bail</option>
                      <option value="civil">Civil & Property Disputes</option>
                      <option value="family">Family Disputes</option>
                      <option value="cheque">Cheque Bounce (NI Act)</option>
                      <option value="banking">Banking & Loan Matters</option>
                      <option value="tax">Tax & GST Matters</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div><label className="label">Message *</label><textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your legal matter..." rows="5" required className="textarea" /></div>
                <button type="submit" className="btn-primary py-3.5 px-8 gap-2"><FiSend className="w-4 h-4" /> Send Message</button>
                <p className="text-xs text-secondary-400">* Required fields. We typically respond within 24 business hours.</p>
              </form>
            </motion.div>
          </div>

          {/* Map / Side */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 overflow-hidden h-64">
              <iframe
                title="RKS Legal Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3597.5!2d85.1!3d25.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sIndu+Enclave+Vijay+Nagar+Patna!5e0!3m2!1sen!2sin!4v1!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-2xl border border-primary-200/50 p-6">
              <h3 className="font-semibold text-secondary-900 mb-2">Need Urgent Legal Help?</h3>
              <p className="text-sm text-secondary-600 mb-4">For urgent legal matters, call us directly for immediate assistance.</p>
              <a href="tel:+917970809406" className="btn-primary py-3 gap-2 w-full"><FiPhone className="w-4 h-4" /> Call Now</a>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">Frequently Asked Questions</h2>
          <p className="text-secondary-500">Find answers to common questions about our services</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-xl border border-secondary-100 overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-secondary-50 transition-colors">
                <span className="font-medium text-secondary-900 pr-4">{faq.q}</span>
                {openFaq === i ? <FiChevronUp className="w-5 h-5 text-secondary-400 flex-shrink-0" /> : <FiChevronDown className="w-5 h-5 text-secondary-400 flex-shrink-0" />}
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 pt-0">
                  <p className="text-sm text-secondary-600 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ContactModern;
